import { HECTARE_TO_ACRE } from 'common/defines/clients';
import { DATE_STANDARD_FORMAT, DATE_VISUALIZE_FORMAT } from 'constants/date';
import ReactECharts from 'echarts-for-react';
import { ILooseFruitData, WeightUnitEnum } from 'interfaces/erms';
import moment from 'moment';
import { FC, useEffect, useMemo, useRef } from 'react';

interface LooseFruitLineChartProps {
  looseFruitData: ILooseFruitData[];
  looseFruitUnit: WeightUnitEnum;
  area: number;
  totalTree: number;
  hoverDatePoint: string;
}

const LooseFruitLineChart: FC<LooseFruitLineChartProps> = ({
  looseFruitData,
  looseFruitUnit,
  area,
  totalTree,
  hoverDatePoint,
}) => {
  const chartRef = useRef<echarts.ECharts | null>(null);

  const xAxisData = looseFruitData.map((item) => item._id);

  // Find the index of hoverDatePoint in xAxisData
  useEffect(() => {
    if (chartRef.current && hoverDatePoint) {
      const dataIndex = xAxisData.findIndex((date) => date === hoverDatePoint);
      // Add a small delay to ensure the chart has updated with new data
      setTimeout(() => {
        if (chartRef.current && dataIndex !== -1) {
          chartRef.current.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: dataIndex,
            position: 'top',
          });
        }
      }, 100); // 100ms delay
    }
  }, [hoverDatePoint, xAxisData, looseFruitUnit]);

  const seriesData = useMemo(() => {
    let defaultSeriesData = looseFruitData.map((item) => item.totalHvLoosefr);
    //default unit of looseFruit is kg

    switch (looseFruitUnit) {
      case WeightUnitEnum.METRIC_TONNE_PER_HECTARE:
        if (!area) return [];
        return defaultSeriesData.map((item) => item / 1000 / area);
      case WeightUnitEnum.KILOGRAM_PER_HECTARE:
        if (!area) return [];
        return defaultSeriesData.map((item) => item / area);
      case WeightUnitEnum.METRIC_TONNE_PER_TREE:
        if (!totalTree) return [];
        return defaultSeriesData.map((item) => item / 1000 / totalTree);
      case WeightUnitEnum.KILOGRAM_PER_TREE:
        if (!totalTree) return [];
        return defaultSeriesData.map((item) => item / totalTree);
      case WeightUnitEnum.METRIC_TONNE_PER_ACRE:
        if (!area) return [];
        return defaultSeriesData.map((item) => item / 1000 / area / HECTARE_TO_ACRE);
      case WeightUnitEnum.KILOGRAM_PER_ACRE:
        if (!area) return [];
        return defaultSeriesData.map((item) => item / area / HECTARE_TO_ACRE);
      case WeightUnitEnum.METRIC_TONNE_PER_HARVEST:
        return looseFruitData.map((item) => {
          const { totalHvLoosefr, totalHarvest } = item;
          return totalHvLoosefr / 1000 / totalHarvest;
        });
      case WeightUnitEnum.KILOGRAM_PER_HARVEST:
        return looseFruitData.map((item) => {
          const { totalHvLoosefr, totalHarvest } = item;
          return totalHvLoosefr / totalHarvest;
        });
      case WeightUnitEnum.METRIC_TONNE:
      default:
        return defaultSeriesData.map((item) => item / 1000);
    }
  }, [area, totalTree, looseFruitData, looseFruitUnit]);

  // Find the index of hoverDatePoint in xAxisData
  useEffect(() => {
    if (chartRef.current && hoverDatePoint) {
      const dataIndex = xAxisData.findIndex((date) => date === hoverDatePoint);
      // Add a small delay to ensure the chart has updated with new data
      setTimeout(() => {
        if (chartRef.current && dataIndex !== -1) {
          chartRef.current.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: dataIndex,
            position: 'top',
          });
        }
      }, 100); // 100ms delay
    }
  }, [hoverDatePoint, xAxisData, looseFruitUnit, seriesData]);

  const option = {
    grid: {
      left: '15%',
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLabel: {
        rotate: 60,
        align: 'center',
        margin: 40,
        formatter: (value: string) => {
          return moment(value, DATE_STANDARD_FORMAT).format(DATE_VISUALIZE_FORMAT);
        },
      },
      axisPointer: {
        show: true,
        snap: true,
        label: {
          show: false,
        },
      },
    },
    yAxis: {
      type: 'value',
    },
    tooltip: {
      trigger: 'axis',
      triggerOn: 'none',
      alwaysShowContent: true,
      formatter: (params: any) => {
        const formattedDate = moment(params[0].name, DATE_STANDARD_FORMAT).format(DATE_VISUALIZE_FORMAT);
        return `${params[0].value.toFixed(3)} (${formattedDate})`;
      },
      backgroundColor: 'rgba(50,50,50,0.7)',
      borderColor: '#333',
      borderWidth: 1,
      textStyle: {
        color: '#fff',
      },
      padding: [5, 10],
      extraCssText: 'box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);',
    },
    series: [
      {
        data: seriesData,
        type: 'line',
        symbol: 'circle',
        symbolSize: 10,
        emphasis: {
          itemStyle: {
            color: '#000000',
            borderWidth: 0,
          },
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      notMerge={true}
      lazyUpdate={true}
      theme={'theme_name'}
      onChartReady={(chartInstance) => {
        chartRef.current = chartInstance;
      }}
    />
  );
};

export default LooseFruitLineChart;
